import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";

const PRESETS = [
  { label: "0.5×", value: 0.5 },
  { label: "1×", value: 1 },
  { label: "2×", value: 2 },
  { label: "3×", value: 3 },
];

interface ScaleControlProps {
  scale: number;
  onChange: (scale: number) => void;
}

export function ScaleControl({ scale, onChange }: ScaleControlProps) {
  const [customInput, setCustomInput] = React.useState("");

  const isPreset = PRESETS.some((p) => p.value === scale);

  // Clear the custom input whenever scale is externally reset to a preset value
  React.useEffect(() => {
    if (isPreset) {
      setCustomInput("");
    }
  }, [scale, isPreset]);

  const handleCustomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomInput(e.target.value);
    const n = parseFloat(e.target.value);
    if (!isNaN(n) && n > 0 && n <= 10) {
      onChange(Math.round(n * 100) / 100);
    }
  };

  const handlePreset = (v: number) => {
    setCustomInput("");
    onChange(v);
  };

  return (
    <div className="space-y-1.5">
      {/* Control row */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-muted-foreground font-medium shrink-0">Scale:</span>
        <div className="flex gap-1">
          {PRESETS.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => handlePreset(p.value)}
              className={cn(
                "h-6 px-2 text-xs rounded border transition-colors",
                scale === p.value
                  ? "bg-primary text-primary-foreground border-primary font-semibold"
                  : "bg-background text-muted-foreground border-border hover:border-primary/50 hover:text-foreground"
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <Input
            type="number"
            min="0.1"
            max="10"
            step="0.5"
            value={customInput}
            onChange={handleCustomChange}
            onFocus={() => setCustomInput(isPreset ? "" : String(scale))}
            placeholder="custom"
            className={cn(
              "h-6 w-20 text-xs px-1.5",
              !isPreset && customInput && "border-primary ring-1 ring-primary/30"
            )}
          />
          <span className="text-xs text-muted-foreground">×</span>
        </div>
      </div>

      {/* Banner when scale ≠ 1 */}
      {scale !== 1 && (
        <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded px-2.5 py-1 text-xs text-blue-700">
          <span>Showing quantities at {scale}× scale</span>
          <button
            type="button"
            onClick={() => { setCustomInput(""); onChange(1); }}
            className="flex items-center gap-0.5 underline underline-offset-2 hover:text-blue-900 ml-auto"
          >
            <X className="h-3 w-3" />
            Reset to 1×
          </button>
        </div>
      )}
    </div>
  );
}
